import { safeInvokeNoArg } from '../../utils/function';
import { getSystemParam } from '../platform';
import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import { Platform } from '../../utils/platform';
import { isAvailable, isInUnifyWebview } from 'jssdk/utils';
import { NativeAPIResponseMessage } from '../../utils/type';
import { PostMessageOption } from 'types/index';
import API from '../../api';
import type {
  Theme,
  Skin,
  ThemeSkin,
  ThemeSkinChangeCallback,
  ThemeChangeCallback,
  EmojiReactionSkin,
} from './types';

// Constants
const SKIN_CHANGE_NOTIFICATION = 'UnifyNotif_OnClientSkinChange';
const DEFAULT_SKIN = 'skin_default' as const;
const DEFAULT_THEME = 'light' as const;
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';
const LIGHT_MODE_QUERY = '(prefers-color-scheme: light)';

// State management
let onThemeSkinChangeFns: ThemeSkinChangeCallback[] = [];
let onThemeChangeFns: ThemeChangeCallback[] = [];
let isThemeSkinChangeRegistered = false;
let fallbackHandler: ((event: MediaQueryListEvent) => void) | null = null;

// MediaQuery initialization with null check
const darkModeMediaQuery: MediaQueryList | null = (() => {
  try {
    return window.matchMedia?.(DARK_MODE_QUERY) || null;
  } catch (e) {
    console.warn('[hybrid/jssdk] MediaQuery initialization failed:', e);
    return null;
  }
})();

/**
 * Utility function to handle MediaQueryList listener compatibility
 * Older browsers only support addListener/removeListener while newer ones support addEventListener/removeEventListener
 */
function addMediaQueryListener(
  mediaQuery: MediaQueryList | null,
  handler: (event: MediaQueryListEvent) => void
): boolean {
  if (!mediaQuery) return false;

  try {
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else if (mediaQuery.addListener) {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
    } else {
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[hybrid/jssdk] Failed to add media query listener:', e);
    return false;
  }
}

function removeMediaQueryListener(
  mediaQuery: MediaQueryList | null,
  handler: (event: MediaQueryListEvent) => void
): boolean {
  if (!mediaQuery) return false;

  try {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener('change', handler);
    } else if (mediaQuery.removeListener) {
      // Fallback for older browsers
      mediaQuery.removeListener(handler);
    } else {
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[hybrid/jssdk] Failed to remove media query listener:', e);
    return false;
  }
}

/**
 * Get theme from MediaQuery with fallback
 */
function getThemeFromMediaQuery(): Theme {
  if (!darkModeMediaQuery) return DEFAULT_THEME;

  try {
    if (darkModeMediaQuery.matches) return 'dark';

    // Check for light mode explicitly
    const lightModeQuery = window.matchMedia?.(LIGHT_MODE_QUERY);
    if (lightModeQuery?.matches) return 'light';

    return DEFAULT_THEME;
  } catch (e) {
    console.warn('[hybrid/jssdk] MediaQuery check failed:', e);
    return DEFAULT_THEME;
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getClientTheme
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientTheme(): Promise<Theme> {
  try {
    const param = await getSystemParam();
    const theme = param.clientTheme as Theme;
    return theme || DEFAULT_THEME;
  } catch (e) {
    console.warn(
      '[hybrid/jssdk] getSystemParam failed, fallback to window.matchMedia',
      e
    );
    return getThemeFromMediaQuery();
  } finally {
    refreshThemeSkinInternalListener();
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getClientSkin
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientSkin(): Promise<Skin> {
  try {
    const param = await getSystemParam();
    const skin = param.skin as Skin;
    return skin || DEFAULT_SKIN;
  } catch (e) {
    console.warn(
      '[hybrid/jssdk] getSystemParam failed for getClientSkin, returning default',
      e
    );
    return DEFAULT_SKIN;
  } finally {
    refreshThemeSkinInternalListener();
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getEmojiReactionSkin
 *
 * [PLATFORM]
 * mac        6.6.0+
 * windows    6.6.0+
 * linux      6.6.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getEmojiReactionSkin(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<EmojiReactionSkin>> {
  await isAvailable('theme.getEmojiReactionSkin');
  return postJsMessage<{}, EmojiReactionSkin>(
    API.getEmojiReactionSkin,
    {},
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.registerThemeChangeListener
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function registerThemeChangeListener(
  onThemeSkinChange: ThemeSkinChangeCallback
): void {
  onClientSkinChange(onThemeSkinChange);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.unregisterThemeChangeListener
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function unregisterThemeChangeListener(
  onThemeSkinChange: ThemeSkinChangeCallback
): void {
  offClientSkinChange(onThemeSkinChange);
}

const onSkinChangeHandler = (message: NativeAPIResponseMessage<ThemeSkin>) => {
  const { returnCode, result } = message;
  if (returnCode === 0 && result) {
    const { skin, theme } = result;
    if (skin) {
      onThemeSkinChangeFns.forEach((fn) => {
        safeInvokeNoArg(() => fn({ skin, theme }));
      });

      // Update global config if available
      if (window.unifyConfig?.systemParam) {
        if (skin) {
          window.unifyConfig.systemParam.skin = skin;
        }
        if (theme) {
          window.unifyConfig.systemParam.clientTheme = theme;
        }
      }
    } else {
      console.warn(
        '[hybrid/jssdk] OnClientSkinChange empty skin message',
        message
      );
    }
  } else {
    console.warn('[hybrid/jssdk] OnClientSkinChange failed', message);
  }
};

function normalizeThemeChangeData(
  event: MediaQueryListEvent
): NativeAPIResponseMessage<ThemeSkin> {
  return {
    returnCode: 0,
    jsCallId: '',
    result: {
      theme: event.matches ? 'dark' : 'light',
      skin: DEFAULT_SKIN,
    },
  };
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.onClientSkinChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onClientSkinChange(
  onSkinChangeFn: ThemeSkinChangeCallback
): void {
  // Prevent duplicate registrations
  if (onThemeSkinChangeFns.includes(onSkinChangeFn)) {
    console.warn(
      '[hybrid/jssdk] Callback already registered for onClientSkinChange'
    );
    return;
  }

  onThemeSkinChangeFns.push(onSkinChangeFn);

  if (!isThemeSkinChangeRegistered) {
    isThemeSkinChangeRegistered = true;
    registerSpecificNotificationHandler(
      SKIN_CHANGE_NOTIFICATION,
      onSkinChangeHandler
    );
  }

  // Setup fallback for non-unify webview environments
  if (!isInUnifyWebview() && darkModeMediaQuery) {
    // Clean up existing handler if any
    if (fallbackHandler) {
      removeMediaQueryListener(darkModeMediaQuery, fallbackHandler);
    }

    fallbackHandler = (e) => {
      onSkinChangeHandler(normalizeThemeChangeData(e));
    };

    addMediaQueryListener(darkModeMediaQuery, fallbackHandler);
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.offClientSkinChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offClientSkinChange(
  onSkinChangeFn: ThemeSkinChangeCallback
): void {
  onThemeSkinChangeFns = onThemeSkinChangeFns.filter(
    (fn) => fn !== onSkinChangeFn
  );

  if (onThemeSkinChangeFns.length === 0) {
    unregisterSpecificNotificationHandler(
      SKIN_CHANGE_NOTIFICATION,
      onSkinChangeHandler
    );
    isThemeSkinChangeRegistered = false;

    // Clean up fallback handler
    if (!isInUnifyWebview() && darkModeMediaQuery && fallbackHandler) {
      removeMediaQueryListener(darkModeMediaQuery, fallbackHandler);
      fallbackHandler = null;
    }
  }
}

const webviewThemeChangeHandler = (e: MediaQueryListEvent) => {
  const isDark = e.matches;
  onThemeChangeFns.forEach((fn) =>
    safeInvokeNoArg(() => fn({ theme: isDark ? 'dark' : 'light' }))
  );
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.onWebviewThemeChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onWebviewThemeChange(onThemeChange: ThemeChangeCallback): void {
  if (Platform.isIOS() || Platform.isMacOS()) {
    // Prevent duplicate registrations
    if (onThemeChangeFns.includes(onThemeChange)) {
      console.warn(
        '[hybrid/jssdk] Callback already registered for onWebviewThemeChange'
      );
      return;
    }

    removeMediaQueryListener(darkModeMediaQuery, webviewThemeChangeHandler);
    addMediaQueryListener(darkModeMediaQuery, webviewThemeChangeHandler);
    onThemeChangeFns.push(onThemeChange);
  } else {
    onClientSkinChange(onThemeChange);
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.offWebviewThemeChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offWebviewThemeChange(
  onThemeChange: ThemeChangeCallback
): void {
  if (Platform.isIOS() || Platform.isMacOS()) {
    onThemeChangeFns = onThemeChangeFns.filter((fn) => fn !== onThemeChange);
    if (onThemeChangeFns.length === 0) {
      removeMediaQueryListener(darkModeMediaQuery, webviewThemeChangeHandler);
    }
  } else {
    offClientSkinChange(onThemeChange);
  }
}

const noopListener = () => {};
export function refreshThemeSkinInternalListener(): void {
  offClientSkinChange(noopListener);
  offWebviewThemeChange(noopListener);
  onClientSkinChange(noopListener);
  onWebviewThemeChange(noopListener);
}

export default {
  getClientTheme,
  getClientSkin,
  getEmojiReactionSkin,
  registerThemeChangeListener,
  unregisterThemeChangeListener,
  onClientSkinChange,
  offClientSkinChange,
  onWebviewThemeChange,
  offWebviewThemeChange,
} as const;
